import { useCallback, useState } from 'react';
import { usePreservedCallback } from '@toss/react';
import { IconButton, XIcon } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useWebsitePreview } from '@/api/ai/website/hooks/useWebsitePreview';
import type { WebsiteData } from '@/api/ai/website/types';
import { DialogBody, DialogFooter, DialogHeader, ResponsiveDialog } from '@/ui_v3/components/dialog';
import { WebsiteConfirmButton } from './components/WebsiteConfirmButton';
import { WebsiteUrlTextField } from './components/WebsiteUrlTextField';
import { WebsitePreviewCard } from './WebsitePreviewCard';
export interface WebsiteUrlModalProps {
  onClose: (data: WebsiteData | null) => void;
  initialWebsiteData?: WebsiteData;
}

/**
 * Website URL 입력 모달
 *
 * overlay-kit Promise 패턴으로 데이터 반환이 가능한 모달
 * - URL 입력 후 Enter 키로 검증 트리거
 * - 검증 성공 시 WebsitePreviewCard 표시
 * - '추가' 버튼 클릭 시 websiteData 반환
 */
export const WebsiteUrlModal = ({
  onClose,
  initialWebsiteData
}: WebsiteUrlModalProps) => {
  const {
    t
  } = useTranslation(['build']);
  const [websiteData, setWebsiteData] = useState<WebsiteData | null>(initialWebsiteData ?? null);

  // Website preview data fetching
  const {
    fetchPreview,
    isLoading
  } = useWebsitePreview();
  const handleFetchWebsiteData = usePreservedCallback(async (url: string) => {
    // 서버가 redirect 정규화한 final_url을 그대로 사용한다 — 사용자가 입력한
    // raw URL로 덮어쓰면 trailing slash / IDN punycode가 어긋나 backend 캐시
    // (scrape_id) 매칭이 빗나갈 수 있다.
    const data = await fetchPreview(url);
    setWebsiteData(data);
    return data;
  });
  const handleCancel = () => {
    setWebsiteData(null);
  };
  const handleClose = () => {
    onClose(null);
  };
  return <ResponsiveDialog onClose={handleClose} size="md" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="WebsiteUrlModal" data-sentry-source-file="WebsiteUrlModal.tsx">
      <DialogHeader title={t('build:build.website.modal.title')} rightSlot={<IconButton className="ml-auto hover:bg-transparent" size="sm" variant="transparent" intent="neutral" disabled={isLoading} onClick={handleClose}>
            <XIcon size="md" />
          </IconButton>} onClose={handleClose} data-sentry-element="DialogHeader" data-sentry-source-file="WebsiteUrlModal.tsx" />

      <DialogBody className="flex flex-col gap-0 px-lg" data-sentry-element="DialogBody" data-sentry-source-file="WebsiteUrlModal.tsx">
        <WebsiteUrlTextField websiteData={websiteData} onSearch={handleFetchWebsiteData} onCancel={handleCancel} isLoading={isLoading} data-sentry-element="WebsiteUrlTextField" data-sentry-source-file="WebsiteUrlModal.tsx" />

        {websiteData && <div className="flex min-h-[316px] flex-col items-center justify-center gap-sm overflow-visible pb-sm pt-md">
            <WebsitePreviewCard websiteData={websiteData} />
          </div>}
      </DialogBody>

      <DialogFooter className="flex justify-end" data-sentry-element="DialogFooter" data-sentry-source-file="WebsiteUrlModal.tsx">
        <WebsiteConfirmButton onConfirm={onClose} websiteData={websiteData} isLoading={isLoading} data-sentry-element="WebsiteConfirmButton" data-sentry-source-file="WebsiteUrlModal.tsx" />
      </DialogFooter>
    </ResponsiveDialog>;
};

/**
 * WebsiteUrlModal을 overlay-kit으로 여는 커스텀 훅
 *
 * overlay-kit Promise 패턴을 사용하여 모달을 열고
 * 사용자가 선택한 웹사이트 데이터를 반환합니다.
 *
 * @returns openWebsiteUrlModal - 모달을 여는 함수, Promise<WebsiteData | null> 반환
 *
 * @example
 * const { openWebsiteUrlModal } = useOpenWebsiteUrlModal();
 * const websiteData = await openWebsiteUrlModal();
 * if (websiteData) {
 *   // 웹사이트가 선택됨
 * }
 */
export const useOpenWebsiteUrlModal = () => {
  const openWebsiteUrlModal = useCallback(async (initialWebsiteData?: WebsiteData) => {
    return overlay.openAsync<WebsiteData | null>(({
      close,
      unmount
    }) => <WebsiteUrlModal onClose={data => {
      unmount();
      close(data);
    }} initialWebsiteData={initialWebsiteData} />);
  }, []);
  return {
    openWebsiteUrlModal
  };
};